import { useRef, useState } from "react";
import { Bot, Camera, FileText, Image as ImageIcon, Loader2, Send } from "lucide-react";
import { api, errText } from "../../lib/api";
import { Badge, CustomerLink, OrderLink, RiskDot, ShopLink, money, shortDate, statusTone } from "./ui";

const LIVE = ["accepted", "preparing", "ready", "out_for_delivery"];

/** One order as a card, with the one-tap actions ops need on a phone. */
export const MobileOrderCard = ({ r, onShare, onChanged, hold }) => {
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const camera = useRef(null);
  const gallery = useRef(null);

  const run = async (label, fn, done) => {
    setBusy(label);
    setMsg("");
    try {
      await fn();
      setMsg(done);
      onChanged?.();
      return true;
    } catch (e) {
      setMsg(errText(e));
      return false;
    } finally {
      setBusy("");
    }
  };

  const accept = () => run("accept", () => api.post(`/admin/orders/${r.id}/accept`), "Accepted.")
    .then((ok) => ok && onShare?.(r, true));

  const dispatch = () => run("dispatch", () => api.post(`/admin/orders/${r.id}/progress`, { status: "out_for_delivery" }),
                             "Out for delivery — buyer and recipient notified.");

  const deliver = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("photo", file);
    fd.append("note", "");
    await run("deliver", () => api.post(`/admin/orders/${r.id}/deliver`, fd,
                                       { headers: { "Content-Type": "multipart/form-data" } }),
              "Delivered — the photo is on its way to the buyer.");
  };

  const deliverWithoutPhoto = async () => {
    const written = window.prompt("No photo? Say how the delivery was confirmed:", "");
    if (written === null) return;
    if (!written.trim()) {
      setMsg("Write a short note saying how the delivery was confirmed.");
      return;
    }
    const fd = new FormData();
    fd.append("note", written.trim());
    await run("deliver", () => api.post(`/admin/orders/${r.id}/deliver`, fd,
                                       { headers: { "Content-Type": "multipart/form-data" } }),
              "Delivered — the buyer has the delivery e-mail.");
  };

  const canAccept = r.status === "awaiting_acceptance" && !!r.florist;
  const canProgress = LIVE.includes(r.status);
  const label = (key, text) => (busy === key ? <Loader2 size={12} className="animate-spin" /> : text);

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm">
            <RiskDot level={r.risk} />{" "}
            <OrderLink number={r.order_number} id={r.id} testid={`mobile-order-link-${r.order_number}`} />
            {r.source === "agentic" ? (
              <Badge tone="brass" className="ms-1.5" testid={`mobile-agent-order-${r.order_number}`} title={r.agent_name || "AI agent"}>
                <Bot size={11} className="inline -mt-0.5 me-0.5" />via AI agent
              </Badge>
            ) : null}
          </p>
          <p className="text-[11px] text-muted truncate">
            {shortDate(r.delivery_date)} · {r.city} · {r.recipient_name}
          </p>
          <p className="text-[11px] text-muted truncate">
            <ShopLink florist={r.florist} /> · <CustomerLink email={r.buyer_email} />
          </p>
        </div>
        <div className="text-end shrink-0">
          <p className="text-sm tabular">{money(r.total_mad)} MAD</p>
          <p className="text-[11px] text-muted tabular">payout {money(r.payout_mad)}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 items-center">
        <Badge tone={statusTone(r.status)}>{r.status === "awaiting_acceptance" ? "awaiting acceptance" : r.status}</Badge>
        {r.acceptance_overdue ? <Badge tone="bad" testid={`mobile-overdue-${r.id}`}>overdue</Badge> : null}
        {hold ? <Badge tone={hold.tone} testid={`mobile-hold-${r.order_number}`}>{hold.label}</Badge> : null}
      </div>

      {canAccept || canProgress ? (
        <div className="flex flex-wrap gap-2 pt-1">
          {canAccept ? (
            <button type="button" className="btn-primary text-[11px] px-3" disabled={!!busy}
                    data-testid={`mobile-accept-${r.id}`} onClick={accept}>{label("accept", "Accept")}</button>
          ) : null}
          {canProgress && r.status !== "out_for_delivery" ? (
            <button type="button" className="btn-ghost text-[11px]" disabled={!!busy}
                    data-testid={`mobile-dispatch-${r.id}`} onClick={dispatch}>{label("dispatch", "Out for delivery")}</button>
          ) : null}
          {canProgress ? (
            <>
              <button type="button" className="btn-ghost text-[11px]" disabled={!!busy}
                      data-testid={`mobile-deliver-camera-${r.id}`} onClick={() => camera.current?.click()}>
                <Camera size={12} className="inline me-1 -mt-0.5" />{label("deliver", "Delivered · photo")}
              </button>
              <button type="button" className="btn-ghost text-[11px]" disabled={!!busy}
                      data-testid={`mobile-deliver-gallery-${r.id}`} onClick={() => gallery.current?.click()}>
                <ImageIcon size={12} className="inline me-1 -mt-0.5" />From gallery
              </button>
              <button type="button" className="btn-ghost text-[11px]" disabled={!!busy}
                      data-testid={`mobile-deliver-nophoto-${r.id}`} onClick={deliverWithoutPhoto}>
                <FileText size={12} className="inline me-1 -mt-0.5" />No photo
              </button>
              <input ref={camera} type="file" accept="image/*" capture="environment" className="hidden"
                     onChange={(e) => deliver(e.target.files?.[0])} />
              <input ref={gallery} type="file" accept="image/*" className="hidden"
                     data-testid={`mobile-deliver-file-${r.id}`} onChange={(e) => deliver(e.target.files?.[0])} />
            </>
          ) : null}
          <button type="button" className="btn-ghost text-[11px]" data-testid={`mobile-share-${r.id}`}
                  onClick={() => onShare?.(r)}>
            <Send size={12} className="inline me-1 -mt-0.5" />Share
          </button>
        </div>
      ) : null}

      {msg ? <p className="text-[11px] text-brass" data-testid={`mobile-order-msg-${r.id}`}>{msg}</p> : null}
    </div>
  );
};

export default MobileOrderCard;
